import type { RequestContext } from "../context/request-context.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import type { TechnicalDocumentSummary, TechnicalDocumentType, TechnicalReviewStatus } from "../../domain/review/technical-document-review.js";
import { TECHNICAL_DOCUMENT_TYPE_LABELS, TECHNICAL_REVIEW_STATUS_LABELS } from "../../domain/review/technical-document-review.js";
import type { ExternalReviewSource, TechnicalDocumentAdapter, TechnicalReviewQueueRow } from "./technical-review-service.js";
import type { LpReportService } from "./lp-report-service.js";
import type { MeReportService } from "./me-report-service.js";
import { ME_ITEM_LABELS } from "../../domain/engineering/me-report-domain.js";
import type { PmReportService } from "./pm-report-service.js";
import type { ConventionalUsReportService } from "./conventional-us-report-service.js";
import type { BscanReportService } from "./bscan-report-service.js";
import type { IntegrityReport, IntegrityReportStatus } from "../../domain/report/integrity-report-domain.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { workflowStatusOfAvr, workflowStatusOfIntegrityReportFull, type EngineeringWorkflowStatus } from "../../domain/review/engineering-workflow-status.js";
import type { AvrReport } from "../../domain/engineering/avr-report-domain.js";

/**
 * Adaptadores dos módulos ao mecanismo central de revisão PLH. Cada um só
 * delega ao serviço do próprio módulo: validação do módulo, snapshot do
 * módulo, status do módulo. Nenhum documento é copiado.
 */

interface CommonNdtReport {
  id: string;
  clientId: string;
  reportNumber: string;
  revision: string;
  status: string;
  clientDisplayName: string;
  projectContract: string;
  unitArea: string;
  equipmentTag: string;
  osNumber: string;
  subOsNumber: string;
  inspectorName: string;
  createdBy: string;
  inspectorProfessionalId?: string | null;
  equipmentTypeLabel?: string;
  testDate?: string;
  projectId?: string | null;
}

function summaryOf(report: CommonNdtReport, route: string): TechnicalDocumentSummary {
  return {
    documentNumber: report.reportNumber,
    revision: report.revision,
    clientId: String(report.clientId),
    client: report.clientDisplayName,
    project: report.projectContract,
    unitArea: report.unitArea,
    tag: report.equipmentTag,
    osNumber: report.osNumber,
    subOsNumber: report.subOsNumber,
    elaboratedBy: report.inspectorName || report.createdBy,
    inspectorProfessionalId: report.inspectorProfessionalId ?? null,
    projectId: report.projectId ?? null,
    equipmentType: report.equipmentTypeLabel || "",
    inspectionDate: report.testDate || "",
    route
  };
}

interface NdtLikeService {
  getReport(context: RequestContext, id: string): Promise<CommonNdtReport | null>;
  validate(context: RequestContext, id: string): Promise<{ valid: boolean; errors: string[] }>;
  buildDocumentSnapshot(context: RequestContext, id: string): Promise<DocumentSnapshot>;
}

class NdtReportAdapter implements TechnicalDocumentAdapter {
  constructor(readonly documentType: TechnicalDocumentType, private readonly service: NdtLikeService, private readonly routeBase: string, private readonly label: string) {}

  private async require(context: RequestContext, id: string): Promise<CommonNdtReport> {
    const report = await this.service.getReport(context, id);
    if (!report) throw new AppError(ErrorCode.NotFound, `${this.label} not found`, { safeMessage: `${this.label} não encontrado.` });
    return report;
  }

  async validate(context: RequestContext, id: string) { return this.service.validate(context, id); }
  async summary(context: RequestContext, id: string) { return summaryOf(await this.require(context, id), `${this.routeBase}/${id}`); }
  async buildSnapshot(context: RequestContext, id: string) { return this.service.buildDocumentSnapshot(context, id); }
  async documentStatus(context: RequestContext, id: string) { return (await this.require(context, id)).status; }
}

export function lpReviewAdapter(service: LpReportService): TechnicalDocumentAdapter {
  return new NdtReportAdapter("LP", service as any, "/ndt/lp", "Relatório LP");
}

/**
 * O ME é liberado ao PLH pelo gate dos ITENS 1 a 4 (`validateForPlhRelease`),
 * mais exigente que o "Validar Dados" da tela — que confere só a medição.
 * Apontar o adapter para o gate garante que a regra valha também para uma
 * chamada direta de RPC: o `disabled` do botão nunca é o controle.
 */
/** Lê o Equipamento para resolver o Projeto de relatórios antigos (fallback canônico). */
export interface MeEquipmentLookup {
  getEquipment(context: RequestContext, equipmentId: string): Promise<{ projectId?: string | null; unitAreaId?: string | null } | null>;
}

export function meReviewAdapter(service: MeReportService, equipments?: MeEquipmentLookup): TechnicalDocumentAdapter {
  const releaseGate: NdtLikeService = {
    getReport: async (context, id) => {
      const report = await (service as any).getReport(context, id);
      if (!report) return null;
      // FALLBACK CANÔNICO (ME → Equipment → Projeto): relatórios criados
      // antes de o ME gravar `projectId` têm só o rótulo do Projeto. Em vez
      // de recusá-los com "documento não vinculado a um Projeto" — o Projeto
      // existe, e a tela sempre o mostrou —, o vínculo é resolvido pelo
      // Equipamento, que é a fonte oficial da hierarquia
      // Cliente → Projeto → Unidade/Área → Equipamento.
      if (!report.projectId && report.equipmentId && equipments) {
        const equipment = await equipments.getEquipment(context, String(report.equipmentId)).catch(() => null);
        if (equipment?.projectId) {
          return { ...report, projectId: equipment.projectId, unitAreaId: report.unitAreaId ?? equipment.unitAreaId ?? null };
        }
      }
      return report;
    },
    buildDocumentSnapshot: (context, id) => service.buildDocumentSnapshot(context, id),
    validate: async (context, id) => {
      const { valid, pendencies } = await service.validateForPlhRelease(context, id);
      return { valid, errors: pendencies.map(p => `${ME_ITEM_LABELS[p.item]}: ${p.message}`) };
    }
  };
  const adapter = new NdtReportAdapter("ME", releaseGate as any, "/ndt/thickness", "Relatório ME");
  // APROVAR = FINALIZAR no ME. `onApproved` fecha o documento no módulo
  // (status FINALIZADO) e `finalizesOnApproval` congela o ciclo de revisão na
  // mesma operação — é isso que faz o botão FINALIZAR RELATÓRIO não aparecer
  // depois, em vez de aparecer desabilitado ou sem efeito.
  (adapter as TechnicalDocumentAdapter).onApproved = async (context, documentId, approvedSnapshot) => {
    await service.finalizeOnPlhApproval(context, documentId, (approvedSnapshot as DocumentSnapshot | null) ?? null);
  };
  (adapter as { finalizesOnApproval?: boolean }).finalizesOnApproval = true;
  return adapter;
}

export function pmReviewAdapter(service: PmReportService): TechnicalDocumentAdapter {
  return new NdtReportAdapter("PM", service as any, "/ndt/pm", "Relatório PM");
}

export function uscReviewAdapter(service: ConventionalUsReportService): TechnicalDocumentAdapter {
  return new NdtReportAdapter("USC", service as any, "/ndt/us-conventional", "Relatório US-Convencional");
}

/**
 * B-SCAN / C-SCAN — mesmo ciclo central do PLH que os demais END.
 *
 * A aprovação FECHA e CONGELA o documento (`finalizeOnPlhApproval`), como
 * no ME: relatório aprovado é imutável e o PDF final é o documento que o
 * PLH analisou, não uma remontagem a partir dos cadastros do momento.
 * `finalizesOnApproval` impede que apareça um segundo "FINALIZAR" depois
 * da aprovação.
 */
export function bscanReviewAdapter(service: BscanReportService): TechnicalDocumentAdapter {
  const adapter = new NdtReportAdapter("BSCAN", service as any, "/ndt/bscan", "Relatório B-Scan / C-Scan");
  (adapter as TechnicalDocumentAdapter).onApproved = async (context, documentId, approvedSnapshot) => {
    await service.finalizeOnPlhApproval(context, documentId, (approvedSnapshot as DocumentSnapshot | null) ?? null);
  };
  (adapter as { finalizesOnApproval?: boolean }).finalizesOnApproval = true;
  return adapter;
}

// ------------------------------------------------------------- EXTERNAL

const RI_TO_REVIEW: Partial<Record<IntegrityReportStatus, TechnicalReviewStatus>> = {
  SUBMITTED_TO_ENGINEERING: "AWAITING_PLH_REVIEW",
  UNDER_ENGINEERING_REVIEW: "PLH_REVIEWING",
  RETURNED: "CORRECTION_REQUESTED",
  IN_CORRECTION: "CORRECTION_REQUESTED",
  APPROVED: "PLH_APPROVED",
  AWAITING_SIGNATURE: "PLH_APPROVED"
};

export interface IntegrityReviewLookups {
  listReports(context: RequestContext): Promise<readonly IntegrityReport[]>;
  describe(context: RequestContext, report: IntegrityReport): Promise<{ client: string; project: string; unitArea: string; tag: string; osNumber: string; subOsNumber: string; elaboratedBy: string; plhName: string; equipmentType?: string; inspectionDate?: string; engineerProfessionalId?: string | null; engineerName?: string; minutaStatus?: string | null }>;
}

/** O RI mantém o seu workflow (Workspace de Engenharia); aqui só é listado na fila unificada como INTEGRIDADE. */
export function integrityReviewSource(lookups: IntegrityReviewLookups): ExternalReviewSource {
  return {
    documentType: "INTEGRITY",
    async listQueue(context: RequestContext): Promise<TechnicalReviewQueueRow[]> {
      const reports = await lookups.listReports(context);
      const rows: TechnicalReviewQueueRow[] = [];
      for (const r of reports) {
        const status = RI_TO_REVIEW[r.status];
        if (!status) continue;
        const d = await lookups.describe(context, r);
        rows.push({
          reviewId: null,
          documentType: "INTEGRITY",
          typeLabel: TECHNICAL_DOCUMENT_TYPE_LABELS.INTEGRITY,
          documentId: String(r.id),
          documentNumber: r.reportNumber || String(r.id),
          revision: r.revision || "00",
          clientId: String(r.clientId),
          client: d.client,
          project: d.project,
          unitArea: d.unitArea,
          tag: d.tag,
          osNumber: d.osNumber,
          subOsNumber: d.subOsNumber,
          elaboratedBy: d.elaboratedBy,
          engineerProfessionalId: d.engineerProfessionalId ?? null,
          engineerName: d.engineerName || "",
          finalizedAt: (r as any).finalization?.finalizedAt ?? null,
          inspectorName: d.elaboratedBy,
          inspectorProfessionalId: r.inspectorId ? String(r.inspectorId) : null,
          equipmentType: d.equipmentType || "",
          inspectionDate: d.inspectionDate || "",
          cycle: (r.status as string) === "RESUBMITTED" || r.returnedAt ? 2 : 1,
          correctedAt: r.status === "IN_CORRECTION" ? r.updatedAt : null,
          signedAt: r.status === "SIGNED" || r.status === "COMPLETED" ? r.updatedAt : null,
          plhProfessionalId: r.engineerPlhId ? String(r.engineerPlhId) : null,
          plhName: d.plhName,
          submittedAt: r.sentToPlhAt || r.updatedAt || r.createdAt || "",
          status,
          statusLabel: TECHNICAL_REVIEW_STATUS_LABELS[status],
          reviewRoute: `/engineering/workspace/${r.id}`,
          documentRoute: `/reports/view/${r.id}`,
          ...(((): { workflowStatus?: EngineeringWorkflowStatus } => { const w = workflowStatusOfIntegrityReportFull(r as any, d.minutaStatus ?? null); return w ? { workflowStatus: w } : {}; })())
        });
      }
      return rows;
    }
  };
}

/** AVR — fila "AVR PARA ANÁLISE": toda AVR do contexto (elaboração, análise, correção, aprovada, reprovada, assinada) com ANALISAR. */
export function avrReviewSource(lookups: { listAvrs(context: RequestContext): Promise<readonly AvrReport[]>; engineerOf?(context: RequestContext, avr: AvrReport): Promise<{ id: string | null; name: string }> }): ExternalReviewSource {
  const toReview = (status: string): TechnicalReviewStatus => (status === "PLH_REVIEWING" ? "PLH_REVIEWING" : status === "CORRECTION_REQUESTED" || status === "REJECTED" ? "CORRECTION_REQUESTED" : status === "PLH_APPROVED" || status === "SIGNED" ? "PLH_APPROVED" : "AWAITING_PLH_REVIEW");
  return {
    documentType: "AVR",
    async listQueue(context: RequestContext): Promise<TechnicalReviewQueueRow[]> {
      const avrs = await lookups.listAvrs(context);
      const rows: TechnicalReviewQueueRow[] = [];
      for (const a of avrs) {
        const eng = lookups.engineerOf ? await lookups.engineerOf(context, a) : { id: a.engineerResponsible?.professionalId ? String(a.engineerResponsible.professionalId) : null, name: a.engineerResponsible?.name || "" };
        const status = toReview(a.status);
        rows.push({
          reviewId: null,
          documentType: "AVR",
          typeLabel: TECHNICAL_DOCUMENT_TYPE_LABELS.AVR,
          documentId: a.id,
          documentNumber: a.avrNumber,
          revision: a.revision,
          clientId: String(a.clientId),
          client: a.clientDisplayName || String(a.clientId),
          project: a.projectContract || "",
          unitArea: a.unitArea || "",
          tag: a.equipmentTag || "",
          osNumber: a.osNumber || "",
          subOsNumber: a.subOsNumber || "",
          elaboratedBy: a.engineerResponsible?.name || a.createdBy || "",
          engineerProfessionalId: eng.id,
          engineerName: eng.name,
          finalizedAt: a.finalization?.finalizedAt ?? null,
          inspectorName: a.engineerResponsible?.name || a.createdBy || "",
          inspectorProfessionalId: a.engineerResponsible?.professionalId ? String(a.engineerResponsible.professionalId) : null,
          equipmentType: a.equipmentTypeLabel || "",
          inspectionDate: a.complementary?.evaluationDate || "",
          cycle: a.plhReview?.cycle ?? 1,
          correctedAt: a.plhReview?.correction?.correctedAt ?? null,
          signedAt: a.signedAt,
          plhProfessionalId: a.plhReview?.plhProfessionalId ? String(a.plhReview.plhProfessionalId) : null,
          plhName: a.plhReview?.plhName || "",
          submittedAt: a.plhReview?.submittedAt || a.updatedAt || a.createdAt || "",
          status,
          statusLabel: TECHNICAL_REVIEW_STATUS_LABELS[status],
          reviewRoute: `/engineering/avr/${a.id}`,
          documentRoute: `/integrity/avr/${a.id}`,
          workflowStatus: workflowStatusOfAvr(a)
        });
      }
      return rows;
    }
  };
}
